<script lang="ts" setup>
import { computed, inject, ref, toRefs, watch } from 'vue';
import VIbanField from '@shared/form/VIbanField/VIbanField.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import { z } from 'zod';
import { isValidIBAN } from 'ibantools';
import { useBlurValidatedField } from '@shared/validation/useBlurValidatedField';
import { toTypedSchema } from '@vee-validate/zod';
import StringField from '@shared/form/StringField/StringField.vue';
import { isDisabledInjectionKey, isReadonlyInjectionKey } from '../keys';

const props = defineProps<{
  id: string;
  isRequired?: boolean;
  tooltip?: string;
  labelOnOwnRow?: boolean;
  isHidden?: boolean;
}>();
const { id, tooltip } = toRefs(props);

const { value: hasDisabledParent } = inject(isDisabledInjectionKey, { value: ref(false) });
const { value: hasReadonlyParent } = inject(isReadonlyInjectionKey, { value: ref(false) });

const required = ref<boolean>();

const ibanSchema = z
  .string()
  .max(34)
  .optional()
  .superRefine((value, ctx) => {
    if (required.value && !value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'De IBAN is verplicht.',
      });
    }

    if (value && value?.length < 15) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'IBAN moet minimaal 15 karakters lang zijn',
      });
    }

    if (!!value && !isValidIBAN(value.toUpperCase())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ongeldige IBAN' });
    }
  });
const {
  errors: ibanErrors,
  value: iban,
  handleBlur: ibanHandleBlur,
  meta: ibanMeta,
  validate: validateIban,
} = useBlurValidatedField(
  'rekening.iban',
  computed(() => toTypedSchema(ibanSchema)),
);

const isBicRequired = computed(() => {
  if (!iban.value || iban.value.length < 2) {
    return false;
  }
  return !iban.value.startsWith('NL');
});
const bicRegex = /^[A-Z]{6}(?:[A-Z0-9]{2}|[A-Z0-9]{5})$/;
const bicSchema = z
  .string()
  .optional()
  .nullable()
  .superRefine((bicValue, ctx) => {
    if (!isBicRequired.value && !bicValue) {
      return;
    }

    if (isBicRequired.value && !bicValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'De BIC is verplicht.',
      });
      return;
    }

    if (!bicRegex.test(bicValue ?? '')) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ongeldige BIC' });
      return;
    }

    if (bicValue && iban.value && bicValue.substring(4, 6) !== iban.value.substring(0, 2)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Landcodes in IBAN en BIC komen niet overeen.',
      });
    }
  });
const {
  errors: bicErrors,
  handleBlur: bicHandleBlur,
  validate: validateBic,
  value: bic,
} = useBlurValidatedField('rekening.bic', toTypedSchema(bicSchema));

watch(
  () => ibanMeta.initialValue,
  (value) => (required.value = props.isRequired || !!value),
  {
    immediate: true,
  },
);

watch(
  () => iban.value,
  () => {
    validateBic();
  },
  {
    immediate: true,
  },
);

watch(
  () => isBicRequired.value,
  (toonBic) => {
    if (!toonBic) {
      bic.value = undefined;
    }
  },
);

watch(
  () => ibanMeta,
  (meta) => {
    if (!meta.touched) {
      validateIban();
      validateBic();
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <FieldRowV2
    label="IBAN"
    :errors="ibanErrors.concat(bicErrors)"
    :label-for="id"
    :required="!hasReadonlyParent && required"
    :tooltip
    :label-on-own-row
    :class="{ hidden: isHidden }"
  >
    <div class="flex w-full flex-col gap-2">
      <VIbanField
        :id
        v-model="iban"
        @blur="ibanHandleBlur"
        :disabled="hasDisabledParent"
        :readonly="hasReadonlyParent"
      />
      <div v-if="isBicRequired">
        <StringField
          v-if="!hasReadonlyParent"
          :model-value="bic"
          @blur="bicHandleBlur"
          @update:model-value="(value) => (bic = value?.toUpperCase())"
          autocomplete
          class="uppercase"
          :disabled="hasDisabledParent"
          :has-error="!!bicErrors.length"
          placeholder="SWIFT-/BIC-code"
          required
        />
        <div v-else>{{ bic || '-' }}</div>
      </div>
    </div>
  </FieldRowV2>
</template>
